import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import {
  ColDef,
  IDatasource,
  IGetRowsParams,
} from "ag-grid-community";
import { ButtonRendererComponent } from "src/app/renderer/button-renderer.component";
import { TableHeaderToolTipComponent } from "src/app/shared/table-header-tool-tip/table-header-tool-tip.component";
import { ServerRequest } from "src/app/_models/global.data.model";
import { SiteService } from "src/app/_services/site.service";
import { UtilServiceService } from "src/app/_services/utilService/util-service.service";
import { ErrorUtil } from "src/app/_utilities/error";
import { SubscriptionUtil } from "src/app/_utilities/subscription";
import { DynamicTableColumnsService } from "src/app/_services/dynamic-table-column.service";
import { Global } from "src/app/_utilities/global";
import { LanguageTranslateService } from "src/app/_services/language-translate.service";

@Component({
  selector: "app-custella-sites-table",
  templateUrl: "./custella-sites-table.component.html",
  styleUrls: ["./custella-sites-table.component.scss"],
  standalone: false,
})
export class CustellaSitesTableComponent
  extends SubscriptionUtil
  implements OnInit, OnChanges, OnDestroy
{
  // table
  public frameworkComponents!: any;
  public gridOptions!: any;
  public defaultColDef!: ColDef;
  public columnDefs: any[] = [];
  private gridParams: any;
  private gridApi: any;
  public overlayNoRowsTemplate: any;

  public totalRecords: any;
  public noData: boolean | null = null;
  public isFilterViewToggle = false;

  @Input() searchValue = "";
  @Input() customerId: any;
  @Input() projectId: any = null;
  @Input() assetsId: any = null;
  @Input() siteIdsToSkip: any[] | null = null;
  public filters = this.returnFilterObject();

  @Input() additionalData: any;
  @Input() translate: any;

  loadTable: any = 0;
  public allSiteTypes: any[] = [];
  public allServiceZones: any[] = [];
  public allSiteCategorys: any[] = [];
  public allGeofences: any[] = [];
  public allProjects: any[] = [];

  ids = {
    customerId: null,
  };

  private filterAPILoaded = false;
  private deleteHasPushed = false;
  sortBy: any;
  sortOrder: any;

  // ✅ Define header ID map as class property - single source of truth
  private readonly headerIdMap: { [colId: string]: string } = {
    'siteNumber':    'Tbl_HeadSitNo',
    'customer.name': 'Tbl_HeadSiteCust',
  };

  constructor(
    private util: UtilServiceService,
    private sitesService: SiteService,
    private errorUtil: ErrorUtil,
    private dynamicTableColumnsService: DynamicTableColumnsService,
    private languageTranslateService: LanguageTranslateService
  ) {
    super();
    this.preInit();
  }

  ngOnInit(): void {
    this.init();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log(changes);

    if (
      changes &&
      changes['searchValue'] &&
      changes['searchValue'].previousValue !== changes['searchValue'].currentValue &&
      changes['searchValue'].currentValue.length > 2
    ) {
      console.log(changes);
      this.refresh();
    }

    if (this.searchValue == "") this.refresh();

    if (
      changes['additionalData'] &&
      changes['additionalData'].currentValue !== changes['additionalData'].previousValue
    ) {
      this.refresh();
    }

    if (
      changes['customerId'] &&
      changes['customerId'].currentValue !== changes['customerId'].previousValue
    ) {
      this.ids.customerId = this.customerId;
    }

    if (
      changes['siteIdsToSkip'] &&
      JSON.stringify(changes['siteIdsToSkip'].previousValue) !== JSON.stringify(changes['siteIdsToSkip'].currentValue)
    ) {
      this.refresh();
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  /******************************************** Pre Init ******************************************/
  private preInit() {
    this.frameworkComponents = {
      buttonRenderer: ButtonRendererComponent,
    };

    this.gridOptions = {
      cacheBlockSize: 25,
      rowModelType: "infinite",
      sortingOrder: ["desc", "asc"],
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit();
      },
      // ✅ Set header IDs after first data render
      onFirstDataRendered: () => {
        Global.setHeaderIds(this.headerIdMap);
      },
      tooltipShowDelay: 100,
    };

    this.defaultColDef = {
      enableRowGroup: false,
      enablePivot: false,
      enableValue: true,
      sortable: true,
      resizable: true,
      filter: false,
      tooltipComponent: TableHeaderToolTipComponent,
    };
  }

  /******************************************** Init ******************************************/
  private init() {
    this.overlayNoRowsTemplate =
      "<div class='not-found'><span>" +
      this.translate?.general.messages.noRecordsFound +
      "</span> </div>";

    this.sitesService.getListColumn("site").subscribe((resp) => {
      if (resp && resp.tableColumnSetups) {
        this.sortBy = resp.sortBy;
        this.sortOrder = resp.sortOrder;
        this.configureColumns(resp.tableColumnSetups);

        // ✅ AG Grid v35: use setGridOption instead of setColumnDefs
        if (this.gridApi) {
          this.gridApi.setGridOption("columnDefs", this.columnDefs);
          this.gridApi.refreshHeader();
          setTimeout(() => {
            this.gridApi.sizeColumnsToFit();
            // ✅ Re-apply IDs after columns load
            Global.setHeaderIds(this.headerIdMap);
          }, 0);
        }
      }

      this.loadTable = 1;
      this.onGridReady(this.gridParams);
      console.log(this.loadTable, "===>>>");
    });
  }

  /******************************************** Configure Columns ******************************************/
  private configureColumns(tableColumns: any[]) {
    // ✅ Reset to avoid duplication on re-init
    this.columnDefs = [];

    tableColumns.forEach((tc) => {
      const column: any = {
        field: tc.field,
        headerName: tc.headerName,
        hide: tc.hide,
        cellClass: tc.cellClass,
        width: 200,
        headerTooltip: "#",
        cellRenderer: <any>null,
        headerComponentParams: <any>{},
      };

      if (tc.translatePath) {
        column.headerName = this.languageTranslateService.getNestedValue(
          this.translate,
          tc.translatePath
        );
      }

      tc.headerTooltip = "#";

      if (tc.field === "siteNumber") {
        column.cellRenderer = function (params: any) {
          if (params.data) {
            return Global.setCellRenderer(params, params.data.rowId, params.data.siteNumber);
          }
        };
      }

      if (tc.field === "customer.name") {
        column.cellRenderer = function (params: any) {
          if (params.data) {
            return Global.setCellRenderer(params, params.data.rowId2, params.data.customer?.name);
          }
        };
      }

      if (tc.field === "active") {
        column.cellRenderer = (params: any) => {
          if (params.data) {
            if (params.data.active) {
              return (
                '<span class="p-mr-2 p-tag p-component p-tag-success"><span class="p-tag-value">' +
                this.translate.general.status.active.singular +
                '</span></span>'
              );
            } else {
              return (
                '<span class="p-mr-2 p-tag p-component p-tag-danger"><span class="p-tag-value text-dark">' +
                this.translate.general.status.inactive.singular +
                '</span></span>'
              );
            }
          }
        };
      }

      this.columnDefs.push(column);
    });
  }

  /******************************************** On Table Ready ******************************************/
  public onGridReady(params: any) {
    if (params) {
      console.log(this.loadTable);
      this.gridParams = params;
      this.gridApi = params.api;
      this.gridApi.sizeColumnsToFit();

      // ✅ AG Grid v35: use IDatasource with IGetRowsParams
      const dataSource: IDatasource = {
        getRows: (dsParams: IGetRowsParams) => {
          if (this.loadTable == 1) {
            this.getServerSideData(dsParams as any);
          }
        },
      };

      this.gridApi.sizeColumnsToFit();
      // ✅ AG Grid v35: use setGridOption instead of setDatasource
      this.gridApi.setGridOption("datasource", dataSource);
    }
  }

  /******************************************** Get Data ******************************************/
  private getServerSideData(agGridGetRowsParams: any) {
    this.gridApi.hideOverlay();
    const serverRequest = this.getRequestParams(agGridGetRowsParams);
    var count: any = serverRequest.startRow;

    if (this.additionalData && this.additionalData.customer) {
      this.ids.customerId = this.additionalData.customer.id;
    } else if (this.customerId !== undefined) {
      this.ids.customerId = this.customerId;
    }

    let from = null;

    if (
      this.additionalData &&
      this.additionalData.data &&
      this.additionalData.data.from === "project"
    ) {
      this.filters.project = Number(this.additionalData.data.id);
    }

    if (
      this.additionalData &&
      this.additionalData.data &&
      this.additionalData.data.from
    ) {
      from = this.additionalData.data.from;
    }

    if (
      this.filters.category_type ||
      this.filters.geofence ||
      this.filters.service_zone ||
      this.filters.type
    ) {
      from = null;
    }

    if (this.searchValue && this.searchValue.length > 0) {
      from = null;
    } else {
      from = this.additionalData?.data?.from;
    }

    const siteCustomerId = this.ids.customerId || this.customerId;
    const siteProjectId = siteCustomerId ? this.filters.project : (this.projectId || this.filters.project);

    this.sitesService
      .getAllSites(
        serverRequest,
        this.searchValue,
        siteCustomerId,
        siteProjectId,
        this.filters.type,
        this.filters.service_zone,
        this.filters.category_type,
        this.filters.geofence,
        from,
        this.siteIdsToSkip?.length ? this.siteIdsToSkip : null,
        this.assetsId || null
      )
      .subscribe(
        (resp) => {
          console.log(resp);
          if (resp) {
            resp.data.forEach((element: any) => {
              count++;
              element.rowId = 'Tbl_RecSitNo' + count;
              element.rowId2 = 'Tbl_RecSiteCust' + count;
              element.buttonEdit = 'Btn_SitEdit' + count;
              element.buttonDelete = 'Btn_SitDel' + count;
            });

            this.setGridData(agGridGetRowsParams, resp.data, resp.total, resp.status);
          }
        },
        (err) => {
          this.errorUtil.setErrorMessage(err);
        }
      );
  }

  /******************************************** Get Params ******************************************/
  private getRequestParams(agGridRequest: any): ServerRequest {
    if (agGridRequest.sortModel.length <= 0) {
      const parameters = { colId: this.sortBy, sort: this.sortOrder };
      agGridRequest.sortModel.push(parameters);
    }

    return {
      startRow: agGridRequest.startRow,
      pageSize: 25,
      filterModel: null,
      sortModel: agGridRequest.sortModel,
    };
  }

  /******************************************** Set Data ******************************************/
  private setGridData(
    agGridGetRowsParams: any,
    resultItems: any[],
    totalCount: number,
    status: string
  ) {
    this.gridApi.hideOverlay();
    this.totalRecords = totalCount;

    if (status === "NO_DATA") {
      this.noData = true;
      this.gridApi.showNoRowsOverlay();
    } else {
      this.noData = false;

      if (resultItems.length === 0) {
        this.gridApi.showNoRowsOverlay();
      } else {
        // ✅ AG Grid v35: successCallback for infinite row model
        agGridGetRowsParams.successCallback(resultItems, totalCount);
      }
    }
  }

  /******************************************** Refresh Table ******************************************/
  public refresh() {
    this.onGridReady(this.gridParams);
  }

  /******************************************** Clear All ******************************************/
  public clearAll() {
    this.filters = this.returnFilterObject();
    this.refresh();
  }

  /******************************************** View Cell ******************************************/
  public viewCell($event: any) {
    const field = $event.colDef.field;

    if (field === "siteNumber") {
      this.sitesService.select($event.data);
    }
  }

  /******************************************** Table Context Menu ******************************************/
  public getContextMenuItems = (params: any) => {
    const col = params.column.colId;
    return this.dynamicTableColumnsService.rightClick(params);
  };

  /******************************************** Returns Filter Object ******************************************/
  private returnFilterObject() {
    return {
      type: null,
      service_zone: null,
      category_type: null,
      geofence: null,
      project: <number | null>null,
    };
  }

  /******************************************** Add All the Subscriptions ******************************************/
  push(obs: any) {
    super.push(obs);
  }
}